import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { featuredArticle } from '../../../mocks/articles';

const HeroSection = () => {
  return (
    <section className="relative min-h-[70vh] sm:min-h-[80vh] lg:min-h-[85vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={featuredArticle.image}
          alt={featuredArticle.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-dark-bg"></div>
        <div className="absolute inset-0 bg-gradient-hero"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 w-full"
          >
            {/* Label */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-gaming/20 border border-accent-cyan/30 rounded-full mb-4 sm:mb-6 backdrop-blur-sm">
              <i className="ri-fire-fill text-orange-500 text-sm sm:text-base"></i>
              <span className="text-white font-inter font-semibold text-xs sm:text-sm">
                TIN NỔI BẬT
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-exo font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-white via-accent-cyan to-white bg-clip-text text-transparent">
                {featuredArticle.title.split(' ').slice(0, 4).join(' ')}
              </span>
              <br />
              <span className="text-white">
                {featuredArticle.title.split(' ').slice(4).join(' ')}
              </span>
            </h1>

            {/* Description */}
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-6 sm:mb-8 max-w-2xl">
              {featuredArticle.excerpt}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Link
                to={`/bai-viet/${featuredArticle.slug}`}
                className="px-8 sm:px-10 py-3 sm:py-4 bg-gradient-gaming text-white font-inter font-bold rounded-xl hover:scale-105 transition-transform shadow-lg shadow-accent-cyan/20 flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base"
              >
                ĐỌC NGAY
                <i className="ri-arrow-right-line"></i>
              </Link>
              <button className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white/30 text-white font-inter font-semibold rounded-xl hover:bg-white/10 transition-all backdrop-blur-sm flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base">
                <i className="ri-play-circle-line text-lg sm:text-xl"></i>
                XEM VIDEO
              </button>
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-4 sm:gap-6 mt-6 sm:mt-8 text-xs sm:text-sm text-gray-400 flex-wrap">
              <div className="flex items-center gap-2">
                <i className="ri-eye-line"></i>
                <span>{featuredArticle.views} lượt xem</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>{featuredArticle.readTime}</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Floating Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative hidden lg:block"
          >
            <div className="relative">
              {/* Floating Card 1 */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute top-10 right-10 bg-dark-card/80 backdrop-blur-lg border border-accent-cyan/30 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-gaming rounded-full flex items-center justify-center">
                    <i className="ri-fire-fill text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-exo font-bold text-white">Top 1</div>
                    <div className="text-xs text-gray-400">Trending</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card 2 */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                className="absolute bottom-20 left-0 bg-dark-card/80 backdrop-blur-lg border border-accent-purple/30 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-accent-purple to-accent-cyan rounded-full flex items-center justify-center">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-exo font-bold text-white">1.2M</div>
                    <div className="text-xs text-gray-400">Lượt xem</div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card 3 */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                className="absolute top-1/2 right-0 bg-dark-card/80 backdrop-blur-lg border border-game-aoe/30 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-game-aoe/20 border border-game-aoe rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-game-aoe"></i>
                  </div>
                  <div>
                    <div className="text-lg font-exo font-bold text-white">5 phút</div>
                    <div className="text-xs text-gray-400">trước</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 text-white/50 flex flex-col items-center gap-2 hidden sm:flex"
      >
        <span className="text-xs font-inter">Cuộn xuống</span>
        <i className="ri-arrow-down-line text-xl"></i>
      </motion.div>
    </section>
  );
};

export default HeroSection;
